import type { HorseParams } from '../race/types';

/**
 * Heritable traits — nine at launch, each hooking exactly one existing
 * knob through this single seam. Mudlark and Iron Constitution ship with
 * phase 5 alongside weather and injuries.
 */

export type TraitId =
  | 'longStrider'
  | 'ironLungs'
  | 'bigHeart'
  | 'coolCustomer'
  | 'frontRunner'
  | 'closer'
  | 'featherweight'
  | 'quickLearner'
  | 'hotBlooded'
  | 'mudlark'
  | 'ironConstitution';

export type TraitDominance = 'dominant' | 'recessive';
export type TraitRarity = 'common' | 'uncommon' | 'rare';
/** When the trait announces itself. */
export type TraitReveal = 'race' | 'training';

export interface TraitDef {
  id: TraitId;
  name: string;
  blurb: string;
  dominance: TraitDominance;
  rarity: TraitRarity;
  reveal: TraitReveal;
}

export const TRAIT_DEFS: Record<TraitId, TraitDef> = {
  longStrider: {
    id: 'longStrider',
    name: 'Long Strider',
    blurb: 'The gate burst carries further (+12% gate phase).',
    dominance: 'dominant',
    rarity: 'common',
    reveal: 'race',
  },
  ironLungs: {
    id: 'ironLungs',
    name: 'Iron Lungs',
    blurb: 'A deeper tank (+10% stamina).',
    dominance: 'recessive',
    rarity: 'uncommon',
    reveal: 'race',
  },
  bigHeart: {
    id: 'bigHeart',
    name: 'Big Heart',
    blurb: 'Coasting recovers faster (+15% regen).',
    dominance: 'recessive',
    rarity: 'rare',
    reveal: 'race',
  },
  coolCustomer: {
    id: 'coolCustomer',
    name: 'Cool Customer',
    blurb: 'Never flinches at the gate; spooks barely register.',
    dominance: 'dominant',
    rarity: 'common',
    reveal: 'race',
  },
  frontRunner: {
    id: 'frontRunner',
    name: 'Front Runner',
    blurb: 'Runs bigger on the lead (+5%), sulks at the back (−3%).',
    dominance: 'dominant',
    rarity: 'common',
    reveal: 'race',
  },
  closer: {
    id: 'closer',
    name: 'Closer',
    blurb: 'Finds another gear in the final stretch from off the pace (+8%).',
    dominance: 'recessive',
    rarity: 'uncommon',
    reveal: 'race',
  },
  featherweight: {
    id: 'featherweight',
    name: 'Featherweight',
    blurb: 'Training costs less energy (−20%).',
    dominance: 'dominant',
    rarity: 'uncommon',
    reveal: 'training',
  },
  quickLearner: {
    id: 'quickLearner',
    name: 'Quick Learner',
    blurb: 'Sessions pay more XP (+15%).',
    dominance: 'recessive',
    rarity: 'rare',
    reveal: 'training',
  },
  hotBlooded: {
    id: 'hotBlooded',
    name: 'Hot Blooded',
    blurb: 'Harder push (+5%), slower recovery (−10%). A gamble.',
    dominance: 'dominant',
    rarity: 'common',
    reveal: 'race',
  },
  mudlark: {
    id: 'mudlark',
    name: 'Mudlark',
    blurb: 'Soft going costs her nothing.',
    dominance: 'dominant',
    rarity: 'uncommon',
    reveal: 'race',
  },
  ironConstitution: {
    id: 'ironConstitution',
    name: 'Iron Constitution',
    blurb: 'Half the injury risk, quicker to heal.',
    dominance: 'recessive',
    rarity: 'rare',
    reveal: 'race',
  },
};

export const TRAIT_IDS: readonly TraitId[] = [
  'longStrider',
  'ironLungs',
  'bigHeart',
  'coolCustomer',
  'frontRunner',
  'closer',
  'featherweight',
  'quickLearner',
  'hotBlooded',
  'mudlark',
  'ironConstitution',
];

/** Dominant needs one allele; recessive needs both copies. */
export function expressedTraits(traitGenes: Record<TraitId, [number, number]>): TraitId[] {
  return TRAIT_IDS.filter((trait) => {
    const [a, b] = traitGenes[trait] ?? [0, 0];
    return TRAIT_DEFS[trait].dominance === 'dominant' ? a + b >= 1 : a >= 1 && b >= 1;
  });
}

/** Race-side effects, applied onto the derived params. */
export function applyRaceTraits(params: HorseParams, traits: readonly TraitId[]): HorseParams {
  const out = { ...params };
  for (const trait of traits) {
    switch (trait) {
      case 'longStrider':
        out.gateBoostS *= 1.12;
        break;
      case 'ironLungs':
        out.staminaMax *= 1.1;
        break;
      case 'bigHeart':
        out.regenPerS *= 1.15;
        break;
      case 'coolCustomer':
        out.noFlinch = true;
        break;
      case 'frontRunner':
        out.frontRunner = true;
        break;
      case 'closer':
        out.closer = true;
        break;
      case 'hotBlooded':
        out.tapImpulse *= 1.05;
        out.regenPerS *= 0.9;
        break;
      default:
        break;
    }
  }
  return out;
}

export function trainingEnergyCost(base: number, traits: readonly TraitId[]): number {
  if (traits.includes('featherweight')) return Math.max(1, Math.round(base * 0.8));
  return base;
}

export function trainingXpMult(traits: readonly TraitId[]): number {
  return traits.includes('quickLearner') ? 1.15 : 1;
}

/** Session modifiers threaded into the pure mini-games via the escrow. */
export function sessionMods(traits: readonly TraitId[]): Record<string, number> {
  const mods: Record<string, number> = {};
  if (traits.includes('coolCustomer')) mods.penDrainMul = 0.5;
  return mods;
}
